export interface BrandingProfile {
  id: string
  userId: string
  
  // Business Information
  businessName: string
  tagline?: string
  businessAddress?: string
  businessPhone?: string
  businessEmail?: string
  businessWebsite?: string
  vatNumber?: string
  registrationNumber?: string
  
  // Visual Branding
  logoUrl?: string
  logoPosition: 'left' | 'center' | 'right'
  primaryColor: string
  secondaryColor: string
  accentColor: string
  
  // Document Settings
  documentFont: 'modern' | 'classic' | 'clean' | 'creative'
  headerStyle: 'minimal' | 'professional' | 'creative' | 'corporate'
  footerText?: string
  watermarkText?: string
  showWatermark: boolean
  
  // Contact Information Display
  showBusinessAddress: boolean
  showBusinessPhone: boolean
  showBusinessEmail: boolean
  showBusinessWebsite: boolean
  showVatNumber: boolean
  showRegistrationNumber: boolean
  
  // Timestamps
  createdAt: string
  updatedAt: string
}

export interface CreateBrandingProfileData {
  businessName: string
  tagline?: string
  businessAddress?: string
  businessPhone?: string
  businessEmail?: string
  businessWebsite?: string
  vatNumber?: string
  registrationNumber?: string
  logoPosition?: 'left' | 'center' | 'right'
  primaryColor?: string
  secondaryColor?: string
  accentColor?: string
  documentFont?: 'modern' | 'classic' | 'clean' | 'creative'
  headerStyle?: 'minimal' | 'professional' | 'creative' | 'corporate'
  footerText?: string
  watermarkText?: string
  showWatermark?: boolean
  showBusinessAddress?: boolean
  showBusinessPhone?: boolean
  showBusinessEmail?: boolean
  showBusinessWebsite?: boolean
  showVatNumber?: boolean
  showRegistrationNumber?: boolean
}

export interface DocumentTemplate {
  id: string
  name: string
  type: 'invoice' | 'contract' | 'nda' | 'proposal' | 'quote' | 'receipt'
  category: 'business' | 'legal' | 'creative'
  description: string
  content: string // HTML/rich text content
  fields: DocumentField[]
  isDefault: boolean
  userId: string
  createdAt: string
  updatedAt: string
}

export interface DocumentField {
  id: string
  name: string
  label: string
  type: 'text' | 'number' | 'date' | 'email' | 'phone' | 'currency' | 'textarea' | 'select'
  placeholder?: string
  options?: string[] // For select fields
  required: boolean
  defaultValue?: string
}

export interface FormalDocument {
  id: string
  userId: string
  templateId: string
  clientId?: string
  
  // Document Info
  documentNumber: string
  title: string
  type: 'contract' | 'nda' | 'proposal' | 'quote' | 'receipt' | 'agreement'
  status: 'draft' | 'sent' | 'viewed' | 'signed' | 'completed' | 'cancelled'
  
  // Content
  content: string // Filled template with actual data
  fieldData: Record<string, any> // Field values
  
  // Dates
  issueDate: string
  expiryDate?: string
  signedDate?: string
  
  // Tracking
  sentAt?: string
  viewedAt?: string
  downloadedAt?: string
  
  // Related data
  client?: {
    id: string
    name: string
    email: string
    company?: string
  }
  template?: DocumentTemplate
  
  // Timestamps
  createdAt: string
  updatedAt: string
}

export interface CreateFormalDocumentData {
  templateId: string
  clientId?: string
  title: string
  type: FormalDocument['type']
  expiryDate?: string
  fieldData: Record<string, any>
}

// Predefined color palettes for branding
export const brandingColorPalettes = {
  professional: {
    name: 'Professional Blue',
    primary: '#2563eb',
    secondary: '#64748b',
    accent: '#0ea5e9'
  },
  creative: {
    name: 'Creative Purple',
    primary: '#7c3aed',
    secondary: '#64748b',
    accent: '#ec4899'
  },
  elegant: {
    name: 'Elegant Black',
    primary: '#1f2937',
    secondary: '#6b7280',
    accent: '#f59e0b'
  },
  nature: {
    name: 'Nature Green',
    primary: '#059669',
    secondary: '#64748b',
    accent: '#84cc16'
  },
  warm: {
    name: 'Warm Orange',
    primary: '#ea580c',
    secondary: '#64748b',
    accent: '#eab308'
  },
  minimal: {
    name: 'Minimal Gray',
    primary: '#374151',
    secondary: '#9ca3af',
    accent: '#6366f1'
  }
}

// Document fonts configuration
export const documentFonts = {
  modern: {
    name: 'Modern Sans',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    description: 'Clean and contemporary'
  },
  classic: {
    name: 'Classic Serif',
    fontFamily: 'Georgia, Times, serif',
    description: 'Traditional and elegant'
  },
  clean: {
    name: 'Clean Minimal',
    fontFamily: 'Inter, Helvetica, sans-serif',
    description: 'Minimalist and readable'
  },
  creative: {
    name: 'Creative Display',
    fontFamily: 'Poppins, sans-serif',
    description: 'Unique and artistic'
  }
} 